import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject, OnDestroy, ViewEncapsulation, Input } from '@angular/core';
import { FormBuilder, FormGroup, AbstractControl } from '@angular/forms';
import { forkJoin, Subscription, of } from 'rxjs';
import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService } from '@nx-fe/services';
import {
  IAgentVideoRecordingSettings,
  IRCCanvasConfig,
  IScreenCaptureConfig,
  IVideoRecordingUserConfig
} from 'xsmodules/remote-control/services/remote-control.interfaces';
import { RemoteControlUserPreferencesService } from 'xsmodules/remote-control/remote-control-view/remote-control-user-preferences/remote-control-user-preferences.service';
import { DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';

export interface IUserPreferencesDialogData {
  isVideoRecordingDisabled: boolean;
  isScreenSettingsVisible: boolean;
}

function mapToVideoResolution(value: string): { width: number; height: number } {
  switch (value) {
    // This will indicate to the agent to use default resolution
    // as is defined by device hardware / Android OS: usually it's
    // the highest available one.
    case 'deviceDefault':
      return { width: 0, height: 0 };
    case 'vga':
      return { width: 480, height: 640 };
    case 'hd':
      return { width: 720, height: 1280 };
  }
}

function mapFromVideoResolution(value: { width: number; height: number }): string {
  if (value.width === 0 && value.height === 0) {
    return 'deviceDefault';
  } else if (value.width === 480 && value.height === 640) {
    return 'vga';
  } else if (value.width === 720 && value.height === 1280) {
    return 'hd';
  }
}

@Component({
  selector: 'app-remote-control-user-preferences-dialog',
  templateUrl: './rc-user-preferences-dialog.component.html',
  styleUrls: ['./rc-user-preferences-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RemoteControlUserPreferencesDialogComponent implements OnInit, OnDestroy {
  public supportedScreenCaptureFormats: any[] = [
    { caption: 'JPEG' },
    { caption: 'BMP' },
    { caption: 'PNG' },
    { caption: 'GIF' },
  ];

  public supportedVideoResolutions = [
    { caption: 'Device Default', value: 'deviceDefault' },
    { caption: '480x640 (VGA)', value: 'vga' },
    { caption: '720x1280 (HD)', value: 'hd' },
  ];

  public supportedFrameRates: any[] = [
    { caption: '30 fps', value: 30 },
    { caption: '25 fps', value: 25 },
    { caption: '20 fps', value: 20 },
    { caption: '15 fps', value: 15 },
    { caption: '10 fps', value: 10 },
    { caption: '5 fps', value: 5 },
  ];

  public supportedVideoBitRates = [
    { caption: '8 Mbps', value: 8000 },
    { caption: '5 Mbps', value: 5000 },
    { caption: '2.5 Mbps', value: 2500 },
    { caption: '1 Mbps', value: 1000 },
  ];

  public supportedAudioBitRates = [
    { caption: '320 kbps', value: 320000 },
    { caption: '256 kbps', value: 256000 },
    { caption: '192 kbps', value: 192000 },
    { caption: '162 kbps', value: 160000 },
    { caption: '128 kbps', value: 128000 },
  ];

  public supportedAudioSamplingRates = [
    { caption: '44.1 KHz', value: 44100 },
    { caption: '48 KHz', value: 48000 },
  ];

  public supportedBitDepths: number[] = [24, 32];
  public form: FormGroup;
  public isLoaded = false;
  public showError = false;
  public isVideoRecordingSettingDisabled = false;
  public isScreenSettingsVisible = true;

  public supportedCanvasColorQualities: any[] = [
    {
      id: '32bpp',
      isImageQualityVisible: true,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ColorDepthMaximum_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ColorDepth32bpp_Caption'
      )})`,
    },
    {
      id: '16bpp',
      isImageQualityVisible: true,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ColorDepthHigh_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ColorDepth16bpp_Caption'
      )})`,
    },
    {
      id: '8bpp',
      isImageQualityVisible: false,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ColorDepthMedium_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ColorDepth8bpp_Caption'
      )})`,
    },
    {
      id: '4bpp',
      isImageQualityVisible: false,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ColorDepthLow_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ColorDepth4bpp_Caption'
      )})`,
    },
    {
      id: '2bpp',
      isImageQualityVisible: false,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ColorDepthVeryLow_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ColorDepth2bpp_Caption'
      )})`,
    },
  ];

  public supportedCanvasImageQualities: any[] = [
    {
      id: 100,
      caption: `${this.saI18nPipe.transform(
        'colorAndQuality_ImageQualityLossless_Caption'
      )} (${this.saI18nPipe.transform('colorAndQuality_ImageQuality100_Caption')})`,
    },
    {
      id: 80,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ImageQualityHigh_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ImageQuality80_Caption'
      )})`,
    },
    {
      id: 70,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ImageQualityMedium_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ImageQuality70_Caption'
      )})`,
    },
    {
      id: 50,
      caption: `${this.saI18nPipe.transform('colorAndQuality_ImageQualityLow_Caption')} (${this.saI18nPipe.transform(
        'colorAndQuality_ImageQuality50_Caption'
      )})`,
    },
  ];

  public get agentRecordingPreferencesEnabled() {
    // Per ticket HD-10523, user should not be allowed to change the agent video recording settings
    return false; // !this.isVideoRecordingSettingDisabled
  }

  private sessionEndedSubscription: Subscription;
  private subscriptionGetConfig: Subscription;
  private subscriptionSave: Subscription;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<RemoteControlUserPreferencesDialogComponent>,
    private remoteControlUserPreferencesService: RemoteControlUserPreferencesService,
    private saI18nPipe: XsI18nPipe,
    public featureToggleService: FeatureToggleService,
    // [mkotormus]: this is temporary dependency until agent video recording settings
    // have storage on the server. Currently they are only stored in a give browser session memory
    // as part of the current RC state, which we need to access here from this service.
    private deviceStateRegistry: DeviceStateRegistryService,
    @Inject(MAT_DIALOG_DATA) public data: IUserPreferencesDialogData
  ) {}

  // Constructs a new FormGroup instance and populates FormControl values via userPreferencesService
  ngOnInit() {
    this.getConfig();
    this.isVideoRecordingSettingDisabled = this.data.isVideoRecordingDisabled;
    this.isScreenSettingsVisible = this.data.isScreenSettingsVisible;
    // Subscribe so that we can close when the RC session ends
    this.sessionEndedSubscription = this.remoteControlUserPreferencesService.sessionEnded.subscribe(() => {
      this.close();
    });
  }

  ngOnDestroy() {
    if (this.subscriptionGetConfig) {
      this.subscriptionGetConfig.unsubscribe();
    }

    if (this.subscriptionSave) {
      this.subscriptionSave.unsubscribe();
    }

    if (this.sessionEndedSubscription) {
      this.sessionEndedSubscription.unsubscribe();
    }
  }

  // Patches the given FormControl's value with its logical not
  public toggleSetting(setting: AbstractControl) {
    if (setting) {
      setting.patchValue(!setting.value);
    }
  }

  // Updates user preferences via service, then closes modal dialog
  public save() {
    const saveVideoRecordingConfigResult = this.remoteControlUserPreferencesService.updateVideoRecordingUserConfig(
      this.getVideoRecordingFormValues()
    );
    const saveScreenshotConfigResult = this.remoteControlUserPreferencesService.updateScreenCaptureConfig(
      this.getScreenCaptureFormValues()
    );
    const saveRCCanvasConfigResult = this.remoteControlUserPreferencesService.updateRCCanvasConfig(
      this.getRCCanvasFormValues()
    );
    const saveVideoWithAudioRecordingConfigResult =
      this.remoteControlUserPreferencesService.updateAgentVideoRecordingPrefs(
        this.getVideoWithAudioRecordingFormValues()
      );

    this.showError = false;
    this.subscriptionSave = forkJoin([
      saveVideoRecordingConfigResult,
      saveScreenshotConfigResult,
      saveRCCanvasConfigResult,
      saveVideoWithAudioRecordingConfigResult,
    ]).subscribe(
      (
        valueArray: [IVideoRecordingUserConfig, IScreenCaptureConfig, IRCCanvasConfig, IAgentVideoRecordingSettings]
      ) => {
        this.updateVideoRecordingFormValues(valueArray[0]);
        this.updateScreenCaptureFormValues(valueArray[1]);
        this.updateRCCanvasFormValues(valueArray[2]);
        this.updateVideoWithAudioRecordingFormValues(valueArray[3]);
        this.dialogRef.close(true);
      },
      (error: any) => {
        console.error('Failed to update remote control user preference configuration');
        console.error(error);
        this.showError = true;
      }
    );
  }

  // Closes the modal dialog without saving values
  public close() {
    this.dialogRef.close();
  }

  public onInputChange(event: any) {
    this.imageQuality.setValue(event);
  }

  // Determined is the Image Quality setting should be visible or not
  public isImageQualityVisible(): boolean {
    const currentSelected = this.supportedCanvasColorQualities.find(c => c.id === this.rcColourQuality.value);
    return currentSelected.isImageQualityVisible;
  }

  // Constructs a new FormGroup instance with default values
  private buildForm(
    videoRecordingConfig: IVideoRecordingUserConfig,
    screenCaptureConfig: IScreenCaptureConfig,
    rcCanvasConfig: IRCCanvasConfig,
    videoWithAudioRecordingConfig: IAgentVideoRecordingSettings
  ) {
    const service = this.remoteControlUserPreferencesService;
    this.form = this.fb.group({
      recordMouseMovement: [service.defaultRecordMouseMovement(videoRecordingConfig.recordMouseMovement)],
      recordMouseClicks: [service.defaultRecordMouseClicks(videoRecordingConfig.recordMouseClicks)],
      includeDeviceSkin: [service.defaultIncludeDeviceSkin(videoRecordingConfig.includeDeviceSkin)],
      screenCaptureFormat: [service.defaultImageFormat(screenCaptureConfig.imageFormat)],
      bitDepth: [service.defaultBitDepth(screenCaptureConfig.bitDepth)],
      imageQuality: [service.defaultImageQuality(screenCaptureConfig.imageQuality)],
      rcColourQuality: [service.defaultRCColourQuality(rcCanvasConfig.colourQuality)],
      rcImageQuality: [service.defaultRCImageQuality(rcCanvasConfig.imageQuality)],
      supportedVideoResolution: [
        mapFromVideoResolution({
          width: videoWithAudioRecordingConfig.screenWidth,
          height: videoWithAudioRecordingConfig.screenHeight,
        }),
      ],
      supportedFrameRate: [videoWithAudioRecordingConfig.videoFrameRate],
      supportedVideoBitRate: [videoWithAudioRecordingConfig.videoBitRate],
      supportedAudioSamplingRate: [videoWithAudioRecordingConfig.audioSamplingRate],
      supportedAudioBitRate: [videoWithAudioRecordingConfig.audioBitRate],
    });
    this.isLoaded = true;
  }

  // Queries userPreferencesService and populates FormControl values in success callback
  private getConfig() {
    const service = this.remoteControlUserPreferencesService;
    const getVideoRecordingConfig = service.getVideoRecordingUserConfig();
    const getRCScreenCaptureConfig = service.getScreenCaptureConfig();
    const getRCCanvasConfig = service.getRCCanvasConfig();

    const rcStateVideoWithAudioRecordingPerfs = this.deviceStateRegistry.getCurrentState().agentVideoRecordingPrefs;
    const videoWithAudioRecordingSettings = of(
      this.remoteControlUserPreferencesService.defaultAgentVideoRecordingPrefs(rcStateVideoWithAudioRecordingPerfs)
    );

    this.showError = false;

    this.subscriptionGetConfig = forkJoin([
      getVideoRecordingConfig,
      getRCScreenCaptureConfig,
      getRCCanvasConfig,
      videoWithAudioRecordingSettings,
    ]).subscribe(
      (
        valueArray: [IVideoRecordingUserConfig, IScreenCaptureConfig, IRCCanvasConfig, IAgentVideoRecordingSettings]
      ) => {
        const videoRecordingConfig: IVideoRecordingUserConfig = valueArray[0];
        const screenCaptureConfig: IScreenCaptureConfig = valueArray[1];
        const rcCanvasConfig: IRCCanvasConfig = valueArray[2];
        const videoWithAudioRecordingConfig: IAgentVideoRecordingSettings = valueArray[3];
        this.buildForm(videoRecordingConfig, screenCaptureConfig, rcCanvasConfig, videoWithAudioRecordingConfig);
      },
      (error: any) => {
        console.error('Failed to get remote control user preference configuration');
        console.error(error);
        this.showError = true;
      }
    );
  }

  // Returns an IAgentVideoRecordingSettings object constructed from current FormControl values
  private getVideoWithAudioRecordingFormValues(): IAgentVideoRecordingSettings {
    return <IAgentVideoRecordingSettings>{
      screenWidth: mapToVideoResolution(this.supportedVideoResolution.value).width,
      screenHeight: mapToVideoResolution(this.supportedVideoResolution.value).height,
      videoFrameRate: this.supportedFrameRate.value,
      audioBitRate: this.supportedAudioBitRate.value,
      audioSamplingRate: this.supportedAudioSamplingRate.value,
      videoBitRate: this.supportedVideoBitRate.value,
    };
  }

  // Returns an IScreenCaptureConfig object constructed from current FormControl values
  private getScreenCaptureFormValues(): IScreenCaptureConfig {
    const screenCaptureConfig: IScreenCaptureConfig = {
      imageFormat: this.screenCaptureFormat.value,
      bitDepth: this.bitDepth.value,
      imageQuality: this.imageQuality.value,
    };
    return screenCaptureConfig;
  }

  // Returns an IVideoRecordingUserConfig object constructed from current FormControl values
  private getVideoRecordingFormValues(): IVideoRecordingUserConfig {
    const videoRecordingConfig: IVideoRecordingUserConfig = {
      includeDeviceSkin: this.includeDeviceSkin.value,
      recordMouseClicks: this.recordMouseClicks.value,
      recordMouseMovement: this.recordMouseMovement.value,
    };
    return videoRecordingConfig;
  }

  // Returns an IRCCanvasConfig object constructed from current FormControl values
  private getRCCanvasFormValues(): IRCCanvasConfig {
    const rcCanvasConfig: IRCCanvasConfig = {
      colourQuality: this.rcColourQuality.value,
      imageQuality: this.rcImageQuality.value,
    };
    return rcCanvasConfig;
  }

  // Patches screen capture FormControl values with given configuration or default if false
  // Patches video recording with audio settings FormControl values with the given configuratio nor default if falsey
  private updateVideoWithAudioRecordingFormValues(videoWithAudioRecordingConfig: IAgentVideoRecordingSettings) {
    const service = this.remoteControlUserPreferencesService;
    const settings = service.defaultAgentVideoRecordingPrefs(videoWithAudioRecordingConfig);
    this.supportedVideoResolution.patchValue(
      mapFromVideoResolution({ width: settings.screenWidth, height: settings.screenHeight })
    );
    this.supportedFrameRate.patchValue(settings.videoFrameRate);
    this.supportedVideoBitRate.patchValue(settings.videoBitRate);
    this.supportedAudioSamplingRate.patchValue(settings.audioSamplingRate);
    this.supportedAudioBitRate.patchValue(settings.audioBitRate);
  }

  // Patches screen capture FormControl values with given configuration or default if falsey
  private updateScreenCaptureFormValues(screenCaptureConfig: IScreenCaptureConfig) {
    const service = this.remoteControlUserPreferencesService;
    this.screenCaptureFormat.patchValue(service.defaultImageFormat(screenCaptureConfig.imageFormat));
    this.bitDepth.patchValue(service.defaultBitDepth(screenCaptureConfig.bitDepth));
    this.imageQuality.patchValue(service.defaultImageQuality(screenCaptureConfig.imageQuality));
  }

  // Patches video recording FormControl values with given configuration or default if false
  private updateVideoRecordingFormValues(videoRecordingConfig: IVideoRecordingUserConfig) {
    const service = this.remoteControlUserPreferencesService;
    this.includeDeviceSkin.patchValue(service.defaultIncludeDeviceSkin(videoRecordingConfig.includeDeviceSkin));
    this.recordMouseClicks.patchValue(service.defaultRecordMouseClicks(videoRecordingConfig.recordMouseClicks));
    this.recordMouseMovement.patchValue(service.defaultRecordMouseMovement(videoRecordingConfig.recordMouseMovement));
  }

  // Patches RC Canvas FormControl values with given configuration or default if false
  private updateRCCanvasFormValues(rcCanvasConfig: IRCCanvasConfig) {
    const service = this.remoteControlUserPreferencesService;
    this.rcColourQuality.patchValue(service.defaultRCColourQuality(rcCanvasConfig.colourQuality));
    this.rcImageQuality.patchValue(service.defaultRCImageQuality(rcCanvasConfig.imageQuality));
  }

  // Aliases to get FormControls
  get recordMouseMovement(): AbstractControl {
    return this.form.get('recordMouseMovement');
  }
  get recordMouseClicks(): AbstractControl {
    return this.form.get('recordMouseClicks');
  }
  get includeDeviceSkin(): AbstractControl {
    return this.form.get('includeDeviceSkin');
  }
  get screenCaptureFormat(): AbstractControl {
    return this.form.get('screenCaptureFormat');
  }
  get bitDepth(): AbstractControl {
    return this.form.get('bitDepth');
  }
  get imageQuality(): AbstractControl {
    return this.form.get('imageQuality');
  }
  get rcColourQuality(): AbstractControl {
    return this.form.get('rcColourQuality');
  }
  get rcImageQuality(): AbstractControl {
    return this.form.get('rcImageQuality');
  }
  get supportedVideoResolution(): AbstractControl {
    return this.form.get('supportedVideoResolution');
  }
  get supportedFrameRate(): AbstractControl {
    return this.form.get('supportedFrameRate');
  }
  get supportedVideoBitRate(): AbstractControl {
    return this.form.get('supportedVideoBitRate');
  }
  get supportedAudioSamplingRate(): AbstractControl {
    return this.form.get('supportedAudioSamplingRate');
  }
  get supportedAudioBitRate(): AbstractControl {
    return this.form.get('supportedAudioBitRate');
  }
}
